import {makeAutoObservable, runInAction, toJS} from 'mobx';
import AthenaAPI from '@/APIs/AthenaAPI';
import {AssistContent, AssistContentItem} from '@/APIs/types';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import {RootStore} from '@/Core/mobx/RootStore';
import {getPreQualMissingFields} from '@/CaseManager/preQualValidation';
import {ApplicationData, Question} from '@agenium-ai/agenium-client-sdk/dist/models';

// Module-level helpers (no MobX action wrapping, safe to call from computeds)
function isPrequalFinancesComplete(ps: ParameterStore): boolean {
    const p = ps.prequal as Record<string, any>;
    const val = (key: string) => p[key]?.value;
    const missing = (v: any) => typeof v !== 'boolean' && (v == null || v === '');

    if (
        missing(val('incomeHouseholdText')) ||
        missing(val('monthlyBudget')) ||
        missing(val('existingCoverage')) ||
        missing(val('recommendedCoverageAmount')) ||
        missing(val('isCurrentlyMilitary'))
    )
        return false;

    if (val('isCurrentlyMilitary') === true && missing(val('currentDeploymentOrders'))) return false;

    if (val('existingCoverage') === true) {
        if (
            missing(val('existingPolicyType')) ||
            missing(val('existingCoverageAmount')) ||
            missing(val('replacingExistingPolicy'))
        )
            return false;
    }

    return true;
}

function isPrequalBudgetBelowFivePercent(ps: ParameterStore): boolean {
    const p = ps.prequal as Record<string, any>;
    const monthlyBudget = parseFloat(p.monthlyBudget?.value);
    const annualHHI = parseFloat(String(p.incomeHouseholdText?.value ?? '').replace(/[,$]/g, ''));
    if (isNaN(monthlyBudget) || isNaN(annualHHI) || annualHHI === 0) return false;
    return monthlyBudget < (annualHHI / 12) * 0.05;
}

const IUL_PRODUCT_TYPES = ['iul', 'lswiul', 'prosper'];

function isQuoteProductTypeIUL(ps: ParameterStore): boolean {
    return IUL_PRODUCT_TYPES.includes(ps.quote?.productType?.value?.toLowerCase());
}

function isSelectedHistoryQuoteIUL(ps: ParameterStore): boolean {
    if (!ps.quoteHistory?.length) return false;
    // singleQuoteData is set when a quote runs in the current session; fall back to
    // the most recent history entry (QuotesHistory auto-selects it as currentQuote)
    const productType =
        ps.singleQuoteData?.productType ??
        ps.quoteHistory.filter((q) => q.singleQuote).at(-1)?.singleQuote?.productType;
    return productType != null && IUL_PRODUCT_TYPES.includes(productType.toLowerCase());
}

function evaluateTrigger(content: AssistContent, ps: ParameterStore, store: ScriptAssistStore): boolean {
    if (!content.active) return false;
    const {trigger} = content;

    let baseMatch = false;
    switch (trigger.type) {
        case 'function': {
            if (!trigger.functionName) return false;
            const fn = store.functionRegistry[trigger.functionName];
            baseMatch = fn ? fn() : false;
            break;
        }
        default:
            return false;
    }

    if (!baseMatch) return false;

    if (trigger.prequalFlow) {
        const isMulti = store.prequalStep !== null;
        if (trigger.prequalFlow === 'multi' && !isMulti) return false;
        if (trigger.prequalFlow === 'single' && isMulti) return false;
    }

    return true;
}

function highestPriority(items: AssistContent[]): AssistContent | null {
    if (!items.length) return null;
    return items.reduce((best, curr) => (curr.priority > best.priority ? curr : best));
}

export class ScriptAssistStore {
    tab: string | null = null;
    triggers: AssistContent[] = [];
    isLoadingContents: boolean = false;

    // Current step in the multi-step prequal flow — null when not in multi-step mode.
    // Set explicitly by PrequalificationMultiStep on mount/step change/unmount so trigger
    // evaluation is not subject to the saveStepData closure race condition.
    prequalStep: number | null = null;

    // True once the Finances header has crossed the top 1/3 threshold (single-step prequal only).
    prequalScrolledPastFinances: boolean = false;

    // True once the Eligibility header has crossed the top 1/3 threshold (single-step prequal only).
    prequalScrolledPastEligibility: boolean = false;

    // ApplicationData from the Agenium SDK — populated only when the prosper tab is active.
    applicationData: ApplicationData[] | null = null;

    // Current question from the Agenium SDK — for use in function registry triggers only.
    currentQuestion: Question | null = null;
    questionIdKeyMap: Record<string, string> = {};

    // Current modal page in the prosper flow — for use in function registry triggers only.
    modalPage: string | null = null;

    // Result of bank account verification on the BANKING_INFO modal page — null until resolved.
    bankVerificationResult: boolean | null = null;

    // Latest calcfocus result from ApprovalInformation — synced whenever a quote is fetched.
    calcFocusData: {
        faceAmount?: number;
        monthlyPremium?: number;
        productName?: string;
        lapseAge?: number;
        quoteAge?: number;
        out?: {name: string; value: any; type: string; fieldName?: string}[];
    } | null = null;

    // When set, setTab calls with a different tab are ignored (used by InstantAppModal to hold
    // the tab at 'prosper' while the quotes page's AgentAssistPanel is also mounted).
    lockedTab: string | null = null;

    // Incremented when the lock is released so AgentAssistPanel re-syncs its tab.
    tabSyncVersion: number = 0;

    // Registry of named boolean functions for 'function' trigger type.
    // Defined as a plain (non-observable) property so the object reference is stable.
    // Functions close over `this` — MobX tracks their observable reads at call time.
    readonly functionRegistry: Record<string, () => boolean> = {
        isPrequalSingleIntro: () => !this.prequalScrolledPastFinances,
        isPrequalSingleEligibility: () => this.prequalScrolledPastEligibility,
        isPrequalSingleFinances: () => this.prequalScrolledPastFinances && !isPrequalFinancesComplete(this.ps),
        isPrequalMultiGoalsAndBasics: () => this.prequalStep === 0,
        isPrequalMultiEligibility: () => this.prequalStep === 1,
        isPrequalMultiFinances: () => this.prequalStep === 2 && !isPrequalFinancesComplete(this.ps),
        isPrequalSingleFinancesLowBudget: () =>
            this.prequalScrolledPastFinances &&
            isPrequalFinancesComplete(this.ps) &&
            isPrequalBudgetBelowFivePercent(this.ps),
        isPrequalMultiFinancesLowBudget: () =>
            this.prequalStep === 2 && isPrequalFinancesComplete(this.ps) && isPrequalBudgetBelowFivePercent(this.ps),
        isPrequalSingleBestFitIUL: () =>
            getPreQualMissingFields(this.ps).length === 0 && this.ps.prequal.recommendedProductPrimary?.value === 'IUL',
        isPrequalMultiBestFitIUL: () =>
            this.prequalStep === 3 && this.ps.prequal.recommendedProductPrimary?.value === 'IUL',
        isQuotesEmptyIUL: () => !this.ps.quoteHistory?.length && isQuoteProductTypeIUL(this.ps),
        isQuotesHistorySelectedIUL: () => isSelectedHistoryQuoteIUL(this.ps),
        // Not all of the following are used yet but added for convenience.
        isProsperStartApplication: () => this.isProsperModalPage('START_APPLICATION'),
        isProsperBeneficiaries: () => this.isProsperModalPage('BENEFICIARIES'),
        isProsperReview: () => this.isProsperModalPage('REVIEW'),
        isProsperEmployer: () => this.isProsperModalPage('EMPLOYER'),
        isProsperExistingLifeInsurance: () => this.isProsperModalPage('EXISTING_LIFE_INSURANCE'),
        isProsperLastDoctorVisit: () => this.isProsperModalPage('LAST_DOCTOR_VISIT'),
        isProsperBirthplace: () => this.isProsperModalPage('BIRTHPLACE'),
        isProsperAddress: () => this.isProsperModalPage('ADDRESS'),
        isProsperVerifyIdentity: () => this.isProsperModalPage('VERIFY_IDENTITY'),
        isProsperBookAppointment: () => this.isProsperModalPage('BOOK_APPOINTMENT'),
        isProsperBankingInfo: () =>
            this.isProsperModalPage('BANKING_INFO') || this.isProsperModalPage('BANKING_INFO_LF'),
        isProsperBankingInfoVerified: () =>
            (this.isProsperModalPage('BANKING_INFO') || this.isProsperModalPage('BANKING_INFO_LF')) &&
            this.bankVerificationResult === true,
        isProsperApprovalInfo: () =>
            this.isProsperModalPage('APPROVAL_INFO') || this.isProsperModalPage('APPROVAL_INFO_LF'),
        isProsperDecisionApproval: () => this.isProsperModalPage('DECISION_APPROVAL'),
        isProsperDecisionDeclined: () => this.isProsperModalPage('DECISION_DECLINED'),
        isProsperDecisionManualUnderwritingWithReason: () =>
            this.isProsperModalPage('DECISION_MANUAL_UNDERWRITING') &&
            !!this.applicationData?.find((t) => t.key === 'Case_UWDecision_Refer_Notes'),
        isProsperDecisionManualUnderwritingWithoutReason: () =>
            this.isProsperModalPage('DECISION_MANUAL_UNDERWRITING') &&
            !this.applicationData?.find((t) => t.key === 'Case_UWDecision_Refer_Notes'),
        isProsperDecisionHardStop: () => this.isProsperModalPage('DECISION_HARD_STOP'),
        isProsperSendDocusign: () => this.isProsperModalPage('SEND_DOCUSIGN'),
        isProsperAllDone: () => this.isProsperModalPage('ALL_DONE'),
        // Application questions
        isProsperAppCriminalOffenses: () =>
            this.isProsperModalPage('APPLICATION') &&
            ['Case_Lifestyle_2', 'Case_Lifestyle_3'].includes(this.getCurrentParentKey() ?? ''),
        isProsperAppBankruptcy: () =>
            this.isProsperModalPage('APPLICATION') &&
            ['Case_Applicant_Bankruptcy'].includes(this.getCurrentParentKey() ?? ''),
        isProsperAppTravelOutsideUS: () =>
            this.isProsperModalPage('APPLICATION') && ['Case_Lifestyle_5'].includes(this.getCurrentParentKey() ?? ''),
        isProsperAppDrinking: () =>
            this.isProsperModalPage('APPLICATION') &&
            ['Case_Lifestyle_6DrinksAvgWeek'].includes(this.getCurrentParentKey() ?? ''),
        isProsperAppPilot: () =>
            this.isProsperModalPage('APPLICATION') && ['Case_NonMedical_1'].includes(this.getCurrentParentKey() ?? ''),
        isProsperAppConditions: () =>
            this.isProsperModalPage('APPLICATION') &&
            ['Case_Medical_HighLevelPointerGroups'].includes(this.currentQuestion?.key ?? ''),
        isProsperAppSpecialistSeen: () =>
            this.isProsperModalPage('APPLICATION') && ['Case_Medical_4'].includes(this.getCurrentParentKey() ?? ''),
        isProsperAppPendingInsurance: () =>
            this.isProsperModalPage('APPLICATION') &&
            ['Case_Applicant_PendingInsurance'].includes(this.getCurrentParentKey() ?? ''),
        isProsperAppGI1Question: () =>
            this.isProsperModalPage('APPLICATION') &&
            ['Case_AgentReport_GI1question'].includes(this.getCurrentParentKey() ?? ''),
    };

    constructor(private parentStore: RootStore) {
        makeAutoObservable(this, {functionRegistry: false});
    }

    // Actions for store fields
    setTab = (tab: string | null) => {
        // Ignore competing tabs while a lock is held.
        if (this.lockedTab !== null && tab !== this.lockedTab) return;
        if (this.tab === tab) return;
        this.tab = tab;
        this.triggers = [];
        this.prequalScrolledPastFinances = false;
        this.prequalScrolledPastEligibility = false;
        this.applicationData = null;
        this.currentQuestion = null;
        this.modalPage = null;
        this.bankVerificationResult = null;
        if (tab) void this.loadContents(tab);
    };

    // Called via useLayoutEffect so the lock is in place before any useEffect setTab calls fire.
    lockTab = (tab: string) => {
        this.lockedTab = tab;
        this.setTab(tab);
    };

    // Called on modal unmount. Increments tabSyncVersion so AgentAssistPanel re-runs its
    // setTab effect and the underlying page (e.g. quotes) re-asserts its tab.
    unlockTab = () => {
        this.lockedTab = null;
        this.tab = null;
        this.triggers = [];
        this.tabSyncVersion += 1;
    };

    setApplicationData = (data: ApplicationData[] | null) => {
        this.applicationData = data;
    };

    setCurrentQuestion = (question: Question | null) => {
        if (question) {
            this.questionIdKeyMap[question.id] = question.key;
        }
        this.currentQuestion = question;
    };

    setModalPage = (page: string | null) => {
        this.modalPage = page;
    };

    setCalcFocusData = (data: ScriptAssistStore['calcFocusData']) => {
        this.calcFocusData = data;
    };

    setBankVerificationResult = (result: boolean | null) => {
        this.bankVerificationResult = result;
    };

    setScrolledPastFinances = (value: boolean) => {
        this.prequalScrolledPastFinances = value;
    };

    setScrolledPastEligibility = (value: boolean) => {
        this.prequalScrolledPastEligibility = value;
    };

    resetScrollState = () => {
        this.prequalScrolledPastFinances = false;
        this.prequalScrolledPastEligibility = false;
    };

    setPrequalStep = (step: number | null) => {
        this.prequalStep = step;
    };

    loadContents = async (tab: string) => {
        runInAction(() => {
            this.isLoadingContents = true;
        });
        try {
            const triggers = await AthenaAPI.getAssistContentsByTab(tab);
            runInAction(() => {
                // Discard results if the tab changed while the fetch was in flight.
                if (this.tab === tab) this.triggers = triggers;
            });
        } catch (e) {
            console.error('[ScriptAssistStore] Failed to load assist contents', e);
        } finally {
            runInAction(() => {
                this.isLoadingContents = false;
            });
        }
    };

    isProsperModalPage = (modalPage: string | null): boolean => {
        return this.modalPage === modalPage;
    };

    getCurrentParentKey = (): string | null => {
        return this.questionIdKeyMap[this.currentQuestion?.parentQuestionId ?? ''] ?? this.currentQuestion?.key ?? null;
    };

    // Getters for computed properties
    private get ps(): ParameterStore {
        return this.parentStore.agentPortalStore.parameterStore;
    }

    get allMatching(): AssistContent[] {
        return this.triggers.filter((c) => evaluateTrigger(c, this.ps, this));
    }

    get matching(): AssistContent | null {
        return highestPriority(this.triggers.filter((c) => evaluateTrigger(c, this.ps, this)));
    }

    get activeScript(): AssistContentItem | null {
        return this.matching?.script ?? null;
    }

    get activeFaq(): AssistContentItem | null {
        return this.matching?.faq ?? null;
    }

    get activeTip(): AssistContentItem | null {
        return this.matching?.tips ?? null;
    }
}
